import React, { useState, useEffect } from 'react';
import { AppointmentEvent } from '../types';
import apiService from '../services/api';

type EventPayload = {
  appointmentDateTime?: string;
  services?: Array<{ serviceName?: string; professionalName?: string | null }>;
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatDateTimeHeader = (dateString: string) => {
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  return { dateStr, timeStr };
};

const parseEventPayload = (eventText: string): EventPayload => {
  try {
    const raw = typeof eventText === 'string' ? JSON.parse(eventText) : {};
    return {
      appointmentDateTime: raw.appointmentDateTime,
      services: Array.isArray(raw.services) ? raw.services : []
    };
  } catch {
    return {};
  }
};

const getModificationChanges = (
  prevPayload: EventPayload,
  currentPayload: EventPayload
): string[] => {
  const changes: string[] = [];
  const prevTime = prevPayload.appointmentDateTime ? new Date(prevPayload.appointmentDateTime).getTime() : null;
  const curTime = currentPayload.appointmentDateTime ? new Date(currentPayload.appointmentDateTime).getTime() : null;
  if (prevTime != null && curTime != null && prevTime !== curTime) {
    const { dateStr, timeStr } = formatDateTimeHeader(currentPayload.appointmentDateTime!);
    changes.push(`Time changed to ${dateStr} at ${timeStr}`);
  }
  const prevServices = prevPayload.services ?? [];
  const curServices = currentPayload.services ?? [];
  if (curServices.length > prevServices.length) {
    const prevNames = new Set(prevServices.map(s => (s.serviceName || '').trim()).filter(Boolean));
    const added = curServices
      .map(s => (s.serviceName || '').trim())
      .filter(name => name && !prevNames.has(name));
    if (added.length === 1) changes.push(`Service added: ${added[0]}`);
    else if (added.length > 1) changes.push(`Services added: ${added.join(', ')}`);
    else changes.push(`${curServices.length - prevServices.length} service(s) added`);
  } else if (curServices.length < prevServices.length) {
    const curNames = new Set(curServices.map(s => (s.serviceName || '').trim()).filter(Boolean));
    const removed = prevServices
      .map(s => (s.serviceName || '').trim())
      .filter(name => name && !curNames.has(name));
    if (removed.length === 1) changes.push(`Service removed: ${removed[0]}`);
    else if (removed.length > 1) changes.push(`Services removed: ${removed.join(', ')}`);
    else changes.push(`${prevServices.length - curServices.length} service(s) removed`);
  }
  const curByService = new Map(
    curServices
      .filter(s => (s.serviceName || '').trim())
      .map(s => [(s.serviceName || '').trim(), s.professionalName ?? null])
  );
  const prevByService = new Map(
    prevServices
      .filter(s => (s.serviceName || '').trim())
      .map(s => [(s.serviceName || '').trim(), s.professionalName ?? null])
  );
  curByService.forEach((curPro, name) => {
    const prevPro = prevByService.get(name);
    if (prevPro !== undefined && prevPro !== curPro) {
      const from = prevPro || 'Unassigned';
      const to = curPro || 'Unassigned';
      changes.push(`Professional reassigned for ${name}: ${from} → ${to}`);
    }
  });
  return changes;
};

const getActivityEventDisplay = (
  event: AppointmentEvent,
  previousEvent?: AppointmentEvent | null
): { title: string; subtext?: string; changes?: string[]; icon: string; iconColor: string } => {
  const byWho = event.createdByFirstName
    ? ` by ${event.createdByFirstName}${event.createdByLastName ? ` ${event.createdByLastName.charAt(0)}.` : ''}`
    : '';
  let parsed: {
    emailType?: string;
    appointmentDateTime?: string;
    services?: any[];
    depositAmount?: number;
    paymentMethod?: string;
  } = {};
  try {
    parsed = typeof event.eventText === 'string' ? JSON.parse(event.eventText) : {};
  } catch {
    // ignore
  }
  switch (event.eventType) {
    case 'CREATED':
      return {
        title: 'Appointment created',
        subtext: byWho.trim() ? byWho : undefined,
        icon: 'bi-calendar-plus',
        iconColor: 'text-blue-600'
      };
    case 'EMAIL_SENT':
      const emailType = parsed.emailType || 'email';
      const emailLabels: Record<string, string> = {
        CONFIRMATION: 'Confirmation email sent to customer',
        CANCELLATION: 'Cancellation email sent to customer',
        RESCHEDULE: 'Reschedule confirmation sent to customer',
        UPDATE: 'Update email sent to customer',
        REMINDER: 'Reminder email sent to customer'
      };
      return {
        title: emailLabels[emailType] ?? 'Email sent to customer',
        icon: 'bi-envelope-check',
        iconColor: 'text-green-600'
      };
    case 'CANCELLED':
      return {
        title: 'Appointment cancelled',
        subtext: byWho.trim() ? byWho : undefined,
        icon: 'bi-x-circle',
        iconColor: 'text-red-600'
      };
    case 'RESCHEDULED': {
      const prevPayload = previousEvent ? parseEventPayload(previousEvent.eventText) : {};
      const curPayload = parseEventPayload(event.eventText);
      const changes = getModificationChanges(prevPayload, curPayload);
      return {
        title: 'Appointment rescheduled',
        subtext: byWho.trim() ? byWho : undefined,
        changes: changes.length > 0 ? changes : undefined,
        icon: 'bi-arrow-repeat',
        iconColor: 'text-amber-600'
      };
    }
    case 'MODIFIED': {
      const prevPayload = previousEvent ? parseEventPayload(previousEvent.eventText) : {};
      const curPayload = parseEventPayload(event.eventText);
      const changes = getModificationChanges(prevPayload, curPayload);
      return {
        title: 'Appointment modified',
        subtext: byWho.trim() ? byWho : undefined,
        changes: changes.length > 0 ? changes : undefined,
        icon: 'bi-pencil-square',
        iconColor: 'text-amber-600'
      };
    }
    case 'DEPOSIT_MARKED_PAID': {
      const amount =
        parsed.depositAmount != null && Number.isFinite(Number(parsed.depositAmount))
          ? Number(parsed.depositAmount)
          : null;
      const amountLabel = amount != null ? `$${amount.toFixed(2)}` : null;
      const methodRaw = typeof parsed.paymentMethod === 'string' ? parsed.paymentMethod.trim() : '';
      const methodLabel = methodRaw
        ? methodRaw.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
        : null;
      const titleBase = amountLabel ? `Deposit marked as paid (${amountLabel})` : 'Deposit marked as paid';
      return {
        title: methodLabel ? `${titleBase} via ${methodLabel}` : titleBase,
        subtext: byWho.trim() ? byWho : undefined,
        icon: 'bi-cash-coin',
        iconColor: 'text-green-600'
      };
    }
    default:
      return {
        title: event.eventType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
        subtext: byWho.trim() ? byWho : undefined,
        icon: 'bi-circle-fill',
        iconColor: 'text-gray-500'
      };
  }
};

export interface AppointmentActivitySectionProps {
  appointmentId: string | undefined;
  /** Increment to trigger a refetch (e.g. after mutations on details page). */
  refreshTrigger?: number;
}

const AppointmentActivitySection: React.FC<AppointmentActivitySectionProps> = ({
  appointmentId,
  refreshTrigger = 0
}) => {
  const [events, setEvents] = useState<AppointmentEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!appointmentId) {
      setEvents([]);
      return;
    }
    setLoading(true);
    apiService
      .getAppointmentEvents(appointmentId)
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [appointmentId, refreshTrigger]);

  const countLabel = !loading && events.length > 0 ? ` (${events.length})` : '';

  return (
    <>
      <button
        type="button"
        onClick={() => setIsExpanded(prev => !prev)}
        className="flex items-center justify-between w-full text-left mb-3 group"
        aria-expanded={isExpanded}
      >
        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-gray-700">
          Activity{countLabel}
        </h3>
        <span className={`text-gray-500 group-hover:text-gray-700 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
          <i className="bi bi-chevron-down text-lg" aria-hidden />
        </span>
      </button>
      {isExpanded && (
        <>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <span className="inline-block w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" aria-hidden />
              Loading activity…
            </div>
          ) : events.length === 0 ? (
            <div className="text-sm text-gray-500 py-2">No activity recorded yet.</div>
          ) : (
            <div className="space-y-3">
              {events.map((event, index) => {
                const previousEvent = index > 0 ? events[index - 1] : null;
                const display = getActivityEventDisplay(event, previousEvent);
                return (
                  <div key={event.id} className="relative pl-6 flex items-start gap-2">
                    <div className={`absolute left-0 top-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 ${display.iconColor}`}>
                      <i className={`bi ${display.icon} text-xs`} aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-900">
                        {display.title}
                        {display.subtext && (
                          <span className="text-gray-600">{display.subtext}</span>
                        )}
                      </div>
                      {display.changes && display.changes.length > 0 && (
                        <ul className="mt-1.5 list-disc list-inside text-xs text-gray-600 space-y-0.5">
                          {display.changes.map((change, i) => (
                            <li key={i}>{change}</li>
                          ))}
                        </ul>
                      )}
                      <div className="text-xs text-gray-500 mt-0.5">
                        {formatDateTimeHeader(event.createdAt).dateStr} • {formatTime(event.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default AppointmentActivitySection;
